import React, { useState } from 'react';
import axios from 'axios';
import { Mail, MapPin, Phone, Send, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { contact } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: '',
  email: '',
  company: '',
  interest: 'Employer — Hire Ghanaian Talent',
  message: '',
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/enquiries`, form, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.status === 201 || res.status === 200) {
        toast.success('Enquiry received', {
          description:
            'Thank you! Our team will get back to you within 1 business day.',
        });
        setForm(initial);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || 'Please try again shortly.';
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d).join(', ')
        : detail;
      toast.error('Could not send enquiry', { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative">
      <div className="jh-dark text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28 grid lg:grid-cols-5 gap-12">
          {/* Left: intro */}
          <div className="lg:col-span-2">
            <span className="jh-eyebrow" style={{ color: '#fff' }}>
              Contact John Herbert Limited
            </span>
            <h2 className="jh-h2 text-white mt-4 mb-6">
              Let’s Build a Compliant, Skilled Workforce Together
            </h2>
            <p className="text-white/70 leading-relaxed mb-10">
              Whether you’re an employer seeking qualified Ghanaian talent or
              a job seeker exploring opportunities abroad, our team is ready
              to help.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#E11D2E] flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="text-[12px] uppercase tracking-widest text-white/50 mb-1">
                    Accra, Ghana
                  </div>
                  <div className="text-white font-semibold">{contact.address}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#E11D2E] flex items-center justify-center flex-shrink-0">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="text-[12px] uppercase tracking-widest text-white/50 mb-1">
                    Email Us
                  </div>
                  <a
                    href={`mailto:${contact.email}`}
                    className="text-white font-semibold hover:text-[#E11D2E] transition-colors"
                  >
                    {contact.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#E11D2E] flex items-center justify-center flex-shrink-0">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-[12px] uppercase tracking-widest text-white/50 mb-1">
                    Licence No.
                  </div>
                  <div className="text-white font-semibold">
                    {contact.licenceNo} — Government of Ghana
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-3">
            <form
              onSubmit={onSubmit}
              className="bg-white/[0.04] border border-white/10 rounded-2xl p-8 md:p-10 backdrop-blur"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[12px] uppercase tracking-widest text-white/60 mb-2 block">
                    Full Name
                  </label>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Jane Doe"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#E11D2E] outline-none py-3 text-white placeholder:text-white/30 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[12px] uppercase tracking-widest text-white/60 mb-2 block">
                    Email
                  </label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="jane@company.com"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#E11D2E] outline-none py-3 text-white placeholder:text-white/30 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[12px] uppercase tracking-widest text-white/60 mb-2 block">
                    Company / Organisation
                  </label>
                  <input
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    placeholder="Acme Health Group"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#E11D2E] outline-none py-3 text-white placeholder:text-white/30 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[12px] uppercase tracking-widest text-white/60 mb-2 block">
                    I am a
                  </label>
                  <select
                    name="interest"
                    value={form.interest}
                    onChange={onChange}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#E11D2E] outline-none py-3 text-white transition-colors"
                  >
                    <option className="text-black">
                      Employer — Hire Ghanaian Talent
                    </option>
                    <option className="text-black">
                      Job Seeker — Work Abroad
                    </option>
                    <option className="text-black">Partner / Regulator</option>
                    <option className="text-black">Media / Other</option>
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label className="text-[12px] uppercase tracking-widest text-white/60 mb-2 block">
                  Tell us about your workforce needs
                </label>
                <textarea
                  required
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  minLength={10}
                  placeholder="Number of positions, roles, timelines, destination country…"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#E11D2E] outline-none py-3 text-white placeholder:text-white/30 transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="jh-btn-primary mt-8 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={17} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send size={17} /> Send Enquiry <ArrowRight size={17} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
